// File: frontend/src/pages/ArticlesPage.tsx
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { List, Search, Tag as TagIcon, ArrowRight, CalendarDays, Loader2, FileWarning, ChevronLeft, ChevronRight, Filter } from 'lucide-react';

import { type Article, type FetchArticlesParams, type TagPublic } from '@/types';
import { getArticles } from '@/api/articleService';
import { getAllTags } from '@/api/tagService';
import { useSiteSettingsStore } from '@/store/settingsStore'; // Import settings store

const ARTICLES_PER_PAGE = 20;

const ArticlesPage = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [allTags, setAllTags] = useState<TagPublic[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>(searchParams.getAll('tags') || []);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [totalArticles, _setTotalArticles] = useState(0); 

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllTagsFilter, setShowAllTagsFilter] = useState(false);
  const [_fetchAttempt, setFetchAttempt] = useState(0); // --- FIX: State to trigger refetch ---

  // Get settings for dynamic header
  const publicSettings = useSiteSettingsStore(state => state.publicSettings);
  const isLoadingSettings = useSiteSettingsStore(state => state.isLoading);

  const pageTitle = isLoadingSettings
    ? "Loading..."
    : publicSettings?.articles_page_header_title || "Vedas Articles & Insights";
  const pageSubtitle = isLoadingSettings
    ? "Loading description..."
    : publicSettings?.articles_page_header_subtitle || "Explore our latest analysis, market commentary, and in-depth articles on finance, technology, and investment strategies.";


  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

useEffect(() => {
    const fetchTags = async () => {
      try {
        const fetchedTags = await getAllTags();
        setAllTags(fetchedTags);
      } catch (err) {
        console.error("Failed to fetch tags:", err);
      }
    };
    fetchTags();
  }, []);

 useEffect(() => {
    const fetchArticlesAndSetParams = async () => {
      setIsLoading(true);
      setError(null);

      // 1. Update the URL to match the current state
      const newSearchParams = new URLSearchParams();
      if (searchTerm) newSearchParams.set('search', searchTerm);
      selectedTagNames.forEach(tag => newSearchParams.append('tags', tag));
      if (currentPage > 1) newSearchParams.set('page', String(currentPage));
      setSearchParams(newSearchParams, { replace: true });

      // 2. Fetch the articles based on the current state
      try {
        const skip = (currentPage - 1) * ARTICLES_PER_PAGE;
        const apiParams: FetchArticlesParams = {
          status: 'published',
          skip,
          limit: ARTICLES_PER_PAGE,
          search: searchTerm || undefined,
          tags: selectedTagNames.length > 0 ? selectedTagNames : undefined,
        };

        // We pass the parameters object IN, and get an Article array OUT.
        const [fetchedArticles] = await Promise.all([
            getArticles(apiParams)
        ]);
        
        setArticles(fetchedArticles);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticlesAndSetParams();
  }, [searchTerm, selectedTagNames, currentPage]); // Re-run only when filters change
  // --- End of FIX ---

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const toggleTag = (tagName: string) => {
    const newSelectedTags = selectedTagNames.includes(tagName)
      ? selectedTagNames.filter(t => t !== tagName)
      : [...selectedTagNames, tagName];
    setSelectedTagNames(newSelectedTags);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

   const handleTryAgain = () => {
    setError(null);
    setFetchAttempt(c => c + 1);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const getFullImageUrl = (relativePath: string | null | undefined): string | undefined => {
    if (!relativePath) return undefined;
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath;
    }
    if (relativePath.startsWith('/static/')) {
      return `${backendBaseUrl}${relativePath}`;
    }
    return `${backendBaseUrl}/static/${relativePath}`;
  };

  const totalPages = totalArticles > 0 ? Math.ceil(totalArticles / ARTICLES_PER_PAGE) : 1;
  const tagsToShow = showAllTagsFilter ? allTags : allTags.slice(0, 7);

    if (isLoading && articles.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading articles...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <FileWarning className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">Error Fetching Articles</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={handleTryAgain} variant="outline">Try Again</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 text-foreground bg-background">
      <header className="mb-10 md:mb-16 text-center border-b border-border/60 pb-10 relative">
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2/3 md:w-1/3 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"></div>
        <List className="h-16 w-16 text-primary mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
          {/* Dynamic Page Title */}
          <span dangerouslySetInnerHTML={{ __html: pageTitle.replace(/Articles|Insights/g, '<span class="text-gradient-maroon-to-gold">$&</span>') || "Vedas <span class=\"text-gradient-maroon-to-gold\">Articles & Insights</span>" }} />
        </h1>
        <p className="mt-4 text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
          {/* Dynamic Page Subtitle */}
          {pageSubtitle}
        </p>
      </header>

      <section className="mb-10 p-6 bg-muted/70 dark:bg-muted/20 rounded-lg shadow-md">
        <div className="grid md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-2">
            <Label htmlFor="search-articles" className="block text-sm font-medium text-foreground mb-1">
              Search Articles
            </Label>
            <div className="relative">
              <Input
                id="search-articles" type="text" placeholder="Search by title or keyword..."
                value={searchTerm} onChange={handleSearchChange}
                className="pl-10 border-border focus:border-primary focus:ring-primary"
              />
              <Search className="absolute left-3 top-1/2 h-5 w-5 text-muted-foreground -translate-y-1/2" />
            </div>
          </div>
          <div>
            <Label className="block text-sm font-medium text-foreground mb-1">
              <Filter className="inline h-4 w-4 mr-1.5 text-primary/80" /> Filter by Tags
            </Label>
             <div className="flex flex-wrap gap-2 items-center mt-2">
              <TagIcon className="h-5 w-5 text-muted-foreground mr-1" />
              {tagsToShow.map(tag => (
                <Badge
                  key={tag.id}
                  variant={selectedTagNames.includes(tag.name) ? 'default' : 'outline'}
                  onClick={() => toggleTag(tag.name)}
                  className="cursor-pointer transition-colors data-[state=selected]:bg-primary data-[state=selected]:text-primary-foreground data-[state=selected]:hover:bg-primary/90 hover:bg-accent/10 dark:hover:bg-accent/20"
                >
                  {tag.name}
                </Badge>
              ))}
              {allTags.length > 7 && (
                <Badge
                  variant="secondary"
                  onClick={() => setShowAllTagsFilter(!showAllTagsFilter)}
                  className="cursor-pointer hover:bg-accent/20"
                >
                  {showAllTagsFilter ? 'Show Less...' : 'More...'}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </section>

      {isLoading && articles.length > 0 && (
        <div className="text-center py-4"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      )}

      {articles.length > 0 ? (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => {
              const fullImageUrl = getFullImageUrl(article.image_url);
              return (
                <Card key={article.id} className="group flex flex-col bg-card text-card-foreground overflow-hidden shadow-lg hover:shadow-xl dark:hover:shadow-primary/10 transition-all duration-300 ease-in-out transform hover:-translate-y-1.5 border border-border/50 hover:border-primary/40 dark:border-border dark:hover:border-primary/30 relative">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent/90 via-primary/90 to-accent/90 scale-x-0 group-hover:scale-x-100 transition-transform duration-400 ease-out origin-center"></div>
                    {fullImageUrl ? (
                      <div className="h-48 w-full overflow-hidden">
                          <img src={fullImageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => (e.currentTarget.style.display = 'none')} />
                      </div>
                    ) : (
                      <div className="h-48 w-full bg-muted/50 dark:bg-muted/20 flex items-center justify-center">
                          <List className="h-16 w-16 text-muted-foreground/70" />
                      </div>
                    )}
                  <div className="relative z-10 flex flex-col flex-grow h-full">
                    <CardHeader className="pt-6 pb-3">
                      <CardTitle className="text-xl text-foreground group-hover:text-primary dark:group-hover:text-accent transition-colors">
                        <Link to={`/articles/${article.slug}`}>{article.title}</Link>
                      </CardTitle>
                      <CardDescription className="text-sm pt-2 space-y-1 text-muted-foreground">
                          <div className="flex items-center text-xs">
                            <CalendarDays className="h-3.5 w-3.5 mr-1.5 text-muted-foreground/80"/>
                            Published on {formatDate(article.published_at)}
                          </div>
                          {article.author_name && <p className="text-xs pt-1">By {article.author_name}</p>}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow py-3">
                      <p className="text-sm text-muted-foreground line-clamp-3 group-hover:text-foreground/90 transition-colors">
                        {article.summary || 'No summary available.'}
                      </p>
                    </CardContent>
                    <CardFooter className="bg-transparent group-hover:bg-muted/20 dark:group-hover:bg-muted/10 p-4 pt-3 transition-colors duration-300">
                      <div className="w-full">
                          <div className="mb-3 flex flex-wrap gap-1.5">
                            {article.tags.slice(0, 3).map(tag => (
                              <Badge key={tag.id} variant="secondary" className="text-xs px-2 py-0.5">{tag.name}</Badge>
                            ))}
                            {article.tags.length > 3 && <Badge variant="secondary" className="text-xs px-2 py-0.5">+{article.tags.length - 3} more</Badge>}
                          </div>
                          <Button asChild variant="outline" className="w-full border-primary text-primary hover:bg-primary/10 hover:text-primary dark:border-primary/70 dark:text-primary/90 dark:hover:bg-primary/20 dark:hover:text-primary transition-colors duration-300">
                            <Link to={`/articles/${article.slug}`}>
                              Read Full Article <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                          </Button>
                      </div>
                    </CardFooter>
                  </div>
                </Card>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="mt-12 flex justify-center items-center space-x-4">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1 || isLoading}
                variant="outline"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <span className="text-muted-foreground">
                Page {currentPage} of {totalPages}
                {totalArticles > 0 && ` (Total: ${totalArticles} articles)`}
              </span>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || isLoading}
                variant="outline"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      ) : (
        !isLoading && (
          <div className="text-center py-16">
            <Search className="h-20 w-20 text-muted-foreground/50 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-foreground mb-2">No Articles Found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search term or filters, or check back later for new content.
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default ArticlesPage;
